import { useContext, useMemo, useState } from 'react';
import { debounce } from 'lodash-es';
import { CgChevronDown } from 'react-icons/cg';
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  ButtonProps,
  CloseButton,
  Grid,
  GridItem,
  Group,
  IconButton,
  Input,
  Menu,
  Popover,
  Portal,
} from '@chakra-ui/react';

import {
  useApplicationAssignments,
  useAssignApplicationUser,
  useResetApplicationAssignUser,
} from '~/core/shared/api/middle-office/application';
import { useUserAssignees } from '~/core/shared/api/middle-office/user';
import { IconTimer } from '~/core/shared/assets';
import { EventCapture } from '~/core/shared/components';
import { AvatarWithRandomColor } from '~/core/shared/components/Avatar';
import { DateTimeDisplayMode } from '~/core/shared/components/DateTimeDisplay/constants/DateTimeDisplayMode';
import { formatDateTime } from '~/core/shared/components/DateTimeDisplay/logic/formatDateTime';
import { EMPTY_ARR } from '~/core/shared/constants';
import { useUserData } from '~/core/shared/contexts/UserContext';
import { Permissions } from '~/core/shared/generated/Permissions';
import { useRequiredParams } from '~/core/shared/hooks';
import { useCan } from '~/core/shared/hooks/useCan';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { Tooltip } from '~/core/shared/lib/chakraUi/components';
import { DocumentUserAssignment, User } from '~/core/shared/types';
import { useDefinition } from '../contexts';
import { ApplicationContext } from './application';

const AssigneeButton = ({
  userName,
  ...props
}: { userName?: string } & ButtonProps) => (
  <Button
    borderColor="gray.muted"
    borderRadius="md"
    color="black"
    colorPalette="gray"
    disabled={props.disabled}
    fontSize="14px"
    {...props}
  >
    {userName && (
      <>
        <AvatarWithRandomColor boxSize={6} fallback={userName} size="sm" />
        {userName}
      </>
    )}
    {!userName && (
      <>
        <Avatar.Root boxSize={6} color="white" size="sm">
          <Avatar.Fallback name="Unassigned" />
        </Avatar.Root>
        Unassigned
      </>
    )}
    <CgChevronDown />
  </Button>
);

const ReadOnlyView = ({
  isChangeAssigneeDenied,
  documentUser,
}: {
  isChangeAssigneeDenied?: boolean;
  documentUser: DocumentUserAssignment | undefined;
}) => (
  <Tooltip
    content="Your assignment permission is restricted by status"
    disabled={!isChangeAssigneeDenied}
    positioning={{ placement: 'top' }}
    showArrow
  >
    <AssigneeButton disabled userName={documentUser?.user?.name} />
  </Tooltip>
);

export const ApplicationAssignees: React.FC = () => {
  const { user } = useUserData();
  const { can } = useCan();

  const { uuid } = useRequiredParams(['uuid']);

  const [searchString, setSearchString] = useState<string>('');
  const [triggerAssigneesQuery, setTriggerAssigneesQuery] =
    useState<boolean>(false);

  const { definition } = useDefinition();

  const { assignUser } = useAssignApplicationUser(definition);
  const { resetAssignUser } = useResetApplicationAssignUser(definition);

  const { userAssignees } = useUserAssignees(
    { search: searchString },
    {
      enabled: triggerAssigneesQuery && can(Permissions.AssigneeUpdate),
    },
  );

  const { assignments } = useApplicationAssignments(uuid, definition, {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const documentUser = useMemo(
    () => assignments?.find((assignment) => assignment.deletedAt === null),
    [assignments],
  );
  const users = userAssignees ?? EMPTY_ARR;
  const documentUsers = assignments;

  const onSearch = debounce((event: any) => {
    setSearchString(event.target.value);
  }, 500);

  const formatDate = useEventHandler((date: Date): string => {
    if (date.getFullYear() < new Date().getFullYear()) {
      return formatDateTime(date, DateTimeDisplayMode.Date);
    }
    return formatDateTime(date, DateTimeDisplayMode.DayMonth);
  });

  const getDocumentUserDates = useEventHandler(
    (item: DocumentUserAssignment): string => {
      const dateFrom = formatDate(new Date(item.createdAt));
      const dateTo = item.deletedAt
        ? formatDate(new Date(item.deletedAt))
        : 'Current';

      return `${dateFrom} - ${dateTo}`;
    },
  );

  const handleAssignUser = useEventHandler(
    async (assignedUser: User): Promise<void> => {
      await assignUser({ uuid, userId: assignedUser.id });
    },
  );

  const handleResetAssignUser = useEventHandler(async (): Promise<void> => {
    await resetAssignUser(uuid);
  });

  const { hooks } = useContext(ApplicationContext);

  const { isChangeAssigneeDenied } = useMemo(() => {
    const hook = hooks.find((h) => h.id === 'changeAssignee');
    const denied = hook?.status === 'deny';
    return {
      isChangeAssigneeDenied: denied,
    };
  }, [hooks]);

  return (
    <Box position="relative">
      <ButtonGroup size="md" variant="outline">
        <Group attached>
          {!isChangeAssigneeDenied && can(Permissions.AssigneeUpdate) ? (
            <Menu.Root>
              <Menu.Trigger asChild>
                <AssigneeButton
                  onClick={() => setTriggerAssigneesQuery(true)}
                  userName={documentUser?.user?.name}
                />
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content maxH={200} overflowY="auto">
                    <EventCapture captureOnKeyDown>
                      <Input
                        onChange={onSearch}
                        pl={2}
                        placeholder="Search"
                        pr={2}
                      />
                    </EventCapture>
                    {searchString === '' && user && (
                      <Menu.Item
                        minH="48px"
                        onClick={() => handleAssignUser(user)}
                        value="assign-to-me"
                      >
                        <AvatarWithRandomColor
                          fallback={user.name}
                          mr="12px"
                          size="sm"
                        />
                        <span>Assign to me</span>
                      </Menu.Item>
                    )}

                    {documentUser?.user !== null && (
                      <Menu.Item
                        minH="48px"
                        onClick={() => handleResetAssignUser()}
                        value="unassign"
                      >
                        <Avatar.Root color="white" mr="12px" size="sm">
                          <Avatar.Fallback />
                        </Avatar.Root>
                        <span>Unassign</span>
                      </Menu.Item>
                    )}

                    {users?.map((item) => (
                      <Menu.Item
                        key={item.id}
                        minH="48px"
                        onClick={() => handleAssignUser(item)}
                        value={`assign-to-${item.id}`}
                      >
                        <AvatarWithRandomColor
                          fallback={item.name}
                          mr="12px"
                          size="sm"
                        />
                        <span>{item.name || 'Unassign'}</span>
                      </Menu.Item>
                    ))}
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          ) : (
            <ReadOnlyView
              documentUser={documentUser}
              isChangeAssigneeDenied={isChangeAssigneeDenied}
            />
          )}

          <Popover.Root positioning={{ placement: 'bottom-start' }}>
            <Popover.Trigger asChild>
              <IconButton
                aria-label="Recent assignees"
                borderColor="gray.muted"
                borderRadius="md"
                colorPalette="gray"
              >
                <IconTimer />
              </IconButton>
            </Popover.Trigger>
            <Portal>
              <Popover.Positioner>
                <Popover.Content borderColor="#E2E8F0" borderRadius="md">
                  <Popover.Header
                    alignItems="center"
                    borderColor="#E2E8F0"
                    display="flex"
                    fontSize={16}
                    fontWeight="700"
                    justifyContent="space-between"
                  >
                    Recent assignees
                    <Popover.CloseTrigger asChild>
                      <CloseButton size="sm" />
                    </Popover.CloseTrigger>
                  </Popover.Header>
                  <Popover.Body fontSize={14} lineHeight="20px">
                    {documentUsers?.map((item) => (
                      <Grid
                        key={item.id}
                        gap={4}
                        templateColumns="repeat(9, 1fr)"
                      >
                        <GridItem colSpan={4}>
                          {item.user?.name || 'Unassigned'}
                        </GridItem>
                        <GridItem colEnd={10} colStart={5} textAlign="right">
                          {getDocumentUserDates(item)}
                        </GridItem>
                      </Grid>
                    ))}
                  </Popover.Body>
                </Popover.Content>
              </Popover.Positioner>
            </Portal>
          </Popover.Root>
        </Group>
      </ButtonGroup>
    </Box>
  );
};
